interface AnalyticsEvent {
  id: string;
  session_id: string;
  event_type: string;
  event_data: Record<string, unknown>;
  referrer: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  device_type: string | null;
  country: string | null;
  created_at: string;
}

// ── Helpers ──────────────────────────────────────────────────────────

const escapeCSV = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  const str = typeof value === 'object' ? JSON.stringify(value) : String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const field = (data: Record<string, unknown> | null | undefined, key: string): string => {
  if (!data || typeof data !== 'object') return '';
  const v = data[key];
  return v == null ? '' : String(v);
};

// ── Page path derivation ─────────────────────────────────────────────

const getPagePath = (e: AnalyticsEvent): string => {
  const d = e.event_data || {};
  const page = field(d, 'page');
  if (page) return page;
  const slug = field(d, 'tool_slug');
  if (slug) return `/herramientas/${slug}`;
  return '';
};

// ── Page classification ──────────────────────────────────────────────

const CORE_PATHS = new Set(['/', '/herramientas', '/faq', '/precios', '/privacidad', '/terminos']);
const EXCLUDED_PATHS = ['/auth', '/analytics', '/configuration'];

const isToolPage = (path: string): boolean =>
  path.startsWith('/herramientas/') && path.length > '/herramientas/'.length;

const isBlogPage = (path: string): boolean => path.startsWith('/blog');

const isCorePage = (path: string): boolean => CORE_PATHS.has(path);

const isExcluded = (path: string): boolean =>
  EXCLUDED_PATHS.some(p => path.startsWith(p));

// ── Tool category mapping ────────────────────────────────────────────

const SLUG_CATEGORY_MAP: Record<string, string> = {
  'quitar-ruido-imagen': 'adjust',
  'eliminar-grano-imagen': 'adjust',
  'mejorar-foto-borrosa': 'adjust',
  'aclarar-imagen-oscura': 'adjust',
  'mejorar-calidad-foto-online': 'adjust',
  'redimensionar-imagen-para-whatsapp': 'resize',
  'redimensionar-imagen-para-cv': 'resize',
  'reducir-imagen-para-cv': 'target-size',
  'reducir-imagen-para-formulario': 'target-size',
  'imagen-a-pdf': 'image-to-pdf',
  'eliminar-fondo': 'background-removal',
};

const getToolCategory = (slug: string): string => {
  if (!slug) return '';
  if (SLUG_CATEGORY_MAP[slug]) return SLUG_CATEGORY_MAP[slug];
  if (slug.startsWith('comprimir-')) return 'compression';
  if (slug.startsWith('convertir-')) return 'conversion';
  if (slug.startsWith('redimensionar-')) return 'resize';
  if (slug.startsWith('reducir-')) return 'target-size';
  if (slug.startsWith('recortar-')) return 'crop';
  if (slug.startsWith('rotar-')) return 'rotate';
  return 'other';
};

// ── Funnel classification ────────────────────────────────────────────

const FUNNEL_MAP: Record<string, string> = {
  page_view: 'view',
  scroll_50: 'view',
  scroll_75: 'view',
  scroll_100: 'view',
  uploader_visible: 'intent',
  upload_click: 'interaction',
  tool_start: 'interaction',
  tool_interaction: 'interaction',
  compression_start: 'interaction',
  compression_auto_start: 'interaction',
  upload_success: 'success',
  compression_success: 'success',
  tool_success: 'success',
  crop_apply: 'success',
  rotate_apply: 'success',
  adjust_apply: 'success',
  pdf_generate: 'success',
  advanced_convert: 'success',
  auto_process_triggered: 'success',
  download_click: 'conversion',
  download_success: 'conversion',
  batch_download_zip: 'conversion',
};

const getStepInFunnel = (eventType: string): string =>
  FUNNEL_MAP[eventType] || '';

// ── Traffic source derivation ────────────────────────────────────────

const ORGANIC_DOMAINS = ['google', 'bing', 'yahoo', 'duckduckgo', 'ecosia', 'baidu', 'yandex'];
const SOCIAL_DOMAINS = ['facebook', 'twitter', 'instagram', 'linkedin', 'tiktok', 'youtube', 'reddit', 'pinterest', 't.co'];

const getTrafficSource = (e: AnalyticsEvent): string => {
  if (e.utm_source) return e.utm_source;
  if (!e.referrer) return 'direct';
  try {
    const host = new URL(e.referrer).hostname.toLowerCase();
    if (ORGANIC_DOMAINS.some(d => host.includes(d))) return 'organic';
    if (SOCIAL_DOMAINS.some(d => host.includes(d))) return 'social';
    if (host.includes('compresorjpeg.com')) return 'direct';
    return 'referral';
  } catch {
    return 'referral';
  }
};

// ── Session sequencing ───────────────────────────────────────────────

interface EnrichedRow {
  event: AnalyticsEvent;
  pagePath: string;
  sessionEventIndex: number;
  isFirst: boolean;
  isLast: boolean;
  nextPagePath: string;
}

const buildSessionSequences = (events: AnalyticsEvent[]): EnrichedRow[] => {
  // Group by session
  const sessions = new Map<string, AnalyticsEvent[]>();
  for (const e of events) {
    const list = sessions.get(e.session_id) || [];
    list.push(e);
    sessions.set(e.session_id, list);
  }

  // Sort each session by timestamp
  for (const list of sessions.values()) {
    list.sort((a, b) => a.created_at.localeCompare(b.created_at));
  }

  // Build enriched rows preserving original event order but with session metadata
  const rowMap = new Map<string, EnrichedRow>();

  for (const [, list] of sessions) {
    // Collect page_view pages for next_page_path lookup
    const pageViews: { index: number; page: string }[] = [];
    list.forEach((e, i) => {
      if (e.event_type === 'page_view') {
        pageViews.push({ index: i, page: getPagePath(e) });
      }
    });

    list.forEach((e, i) => {
      // Find next page_view's path after current index
      let nextPage = '';
      for (const pv of pageViews) {
        if (pv.index > i) {
          nextPage = pv.page;
          break;
        }
      }

      rowMap.set(e.id, {
        event: e,
        pagePath: getPagePath(e),
        sessionEventIndex: i + 1,
        isFirst: i === 0,
        isLast: i === list.length - 1,
        nextPagePath: nextPage,
      });
    });
  }

  // Return in original events order
  return events
    .map(e => rowMap.get(e.id)!)
    .filter(Boolean);
};

// ── CSV column definitions ───────────────────────────────────────────

const HEADERS = [
  'event_timestamp',
  'session_id',
  'event_type',
  'step_in_funnel',
  'page_path',
  'full_url',
  'is_tool_page',
  'is_blog_page',
  'is_core_page',
  'tool_slug',
  'tool_category',
  'referrer',
  'traffic_source',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'device_type',
  'country',
  'session_event_index',
  'is_first_event_in_session',
  'is_last_event_in_session',
  'next_page_path',
  'original_size_kb',
  'final_size_kb',
  'compression_ratio',
  'processing_time_ms',
  'time_to_upload_ms',
  'upload_method',
  'file_type',
];

const SITE_DOMAIN = 'https://compresorjpeg.com';

// ── Main export function ─────────────────────────────────────────────

export const exportEventsToCSV = (events: AnalyticsEvent[], filename: string) => {
  // Filter out excluded paths
  const filtered = events.filter(e => {
    const path = getPagePath(e);
    return !path || !isExcluded(path);
  });

  // Build session sequences
  const rows = buildSessionSequences(filtered);

  const csvRows = rows.map(({ event: e, pagePath, sessionEventIndex, isFirst, isLast, nextPagePath }) => {
    const d = e.event_data || {};
    const toolSlug = field(d, 'tool_slug');

    return [
      e.created_at,
      e.session_id,
      e.event_type,
      getStepInFunnel(e.event_type),
      pagePath,
      pagePath ? `${SITE_DOMAIN}${pagePath}` : '',
      isToolPage(pagePath),
      isBlogPage(pagePath),
      isCorePage(pagePath),
      toolSlug,
      getToolCategory(toolSlug),
      e.referrer || '',
      getTrafficSource(e),
      e.utm_source || '',
      e.utm_medium || '',
      e.utm_campaign || '',
      e.device_type || '',
      e.country || '',
      sessionEventIndex,
      isFirst,
      isLast,
      nextPagePath,
      field(d, 'original_size_kb'),
      field(d, 'compressed_size_kb'),
      field(d, 'reduction_percent'),
      field(d, 'processing_time_ms'),
      field(d, 'time_to_upload_ms'),
      field(d, 'upload_method'),
      field(d, 'file_type'),
    ]
      .map(escapeCSV)
      .join(',');
  });

  const csv = [HEADERS.join(','), ...csvRows].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
